import { ACCOUNT_VERIFICATION_STALE_AFTER_MS } from "../constants/verification.constants";
import type { AccountVerificationResponse } from "../types/verification-api.types";
import { VerificationService } from "./verificationService";

// ─── Account-verification memo ────────────────────────────────────────────────
// The Wallets banner, the Settings section and the dispersions gate all mount within seconds of each other
// and all ask the same question: "what may this organization do right now?". One request per short window
// keeps them on a single GET and, seeded synchronously into the hook, keeps the second surface from
// flashing an empty state. Kept out of the React hook on purpose: cache policy is plain logic and is worth
// testing without a DOM (same shape as `ensurePseBanks`).
//
// The key is `${userId}:${organizationId}` so a re-login as another member never reads the previous
// organization's verdict.

interface Entry {
  data: AccountVerificationResponse;
  fetchedAt: number;
}

const entries = new Map<string, Entry>();
const inflight = new Map<string, Promise<AccountVerificationResponse>>();
// Bumped on every invalidation: a request that was already in flight when the memo was invalidated must not
// re-cache its (pre-invalidation) verdict when it lands.
let generation = 0;

/**
 * The memoized verdict for `key`, or `null` when there is none or it is stale. Synchronous, for state
 * seeding.
 */
export function peekAccountVerification(key: string): AccountVerificationResponse | null {
  const entry = entries.get(key);
  if (!entry) return null;
  return Date.now() - entry.fetchedAt < ACCOUNT_VERIFICATION_STALE_AFTER_MS ? entry.data : null;
}

/**
 * The organization's verification, fetched at most once per window.
 *
 * Concurrent callers share one request, a failed request is never cached (the next call retries), and `force`
 * bypasses the memo for an explicit refresh.
 */
export function ensureAccountVerification(key: string, force = false): Promise<AccountVerificationResponse> {
  if (!force) {
    const cached = peekAccountVerification(key);
    if (cached) return Promise.resolve(cached);
    const pending = inflight.get(key);
    if (pending) return pending;
  }

  const requestGeneration = generation;
  const request = VerificationService.getAccountVerification()
    .then((response) => {
      if (requestGeneration === generation) {
        entries.set(key, { data: response.data, fetchedAt: Date.now() });
      }
      return response.data;
    })
    .finally(() => {
      if (inflight.get(key) === request) inflight.delete(key);
    });
  inflight.set(key, request);
  return request;
}

/**
 * Drops every memoized verdict. Called by the onboarding flow on each status change and by the dispersions
 * wizard when a payout is refused with `organization_verification_required`, so the next mount re-reads.
 */
export function invalidateAccountVerification(): void {
  generation += 1;
  entries.clear();
  inflight.clear();
}
